"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import {
  useEffect,
  useEffectEvent,
  useId,
  useLayoutEffect,
  useState,
} from "react";
import servicesMenu from "@/components/menu/servicesMenu";
import sectorsMenu from "@/components/menu/sectorsMenu";
import companyMenu from "@/components/menu/companyMenu";
import ourPlatformMenu from "@/components/menu/ourPlatformMenu";
import FAIcon from "../icons/faIcon";

const SHELL_SPRING = {
  type: "spring",
  stiffness: 220,
  damping: 24,
  mass: 0.9,
};

const PANEL_SPRING = {
  type: "spring",
  stiffness: 260,
  damping: 26,
  mass: 0.86,
};

const DROPDOWN_FADE_TRANSITION = {
  duration: 0.14,
  ease: "easeOut",
};

const PANEL_RESIZE_TRANSITION = {
  duration: 0.28,
  ease: [0.22, 1, 0.36, 1],
};

const INSTANT_TRANSITION = {
  duration: 0,
};

const MOBILE_BREAKPOINT_QUERY = "(max-width: 1023px)";
const DROPDOWN_SHADOW = "shadow-3xl shadow-black/20";

const NAV_ITEMS = [servicesMenu, sectorsMenu, ourPlatformMenu, companyMenu];

function ChevronDownIcon({ className = "" }) {
  return <FAIcon icon="chevron-down" className={className} noMargin />;
}

function MenuIcon({ className = "" }) {
  return <FAIcon icon="bars" className={className} noMargin />;
}

function CloseIcon({ className = "" }) {
  return <FAIcon icon="xmark" className={className} noMargin />;
}

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const [activeMenu, setActiveMenu] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState("");
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [motionPathname, setMotionPathname] = useState(pathname);
  const menuId = useId();
  const isHomePage = pathname === "/";
  const isCompact = !isHomePage || isScrolled;
  const suppressDesktopMotion = motionPathname !== pathname;
  const desktopShellTransition = suppressDesktopMotion
    ? INSTANT_TRANSITION
    : SHELL_SPRING;
  const desktopShellTransitionClass = suppressDesktopMotion
    ? "transition-none"
    : "transition-[background-color,border-color,box-shadow,backdrop-filter,max-width] duration-300";
  const mobileTransitionClass = isMobileViewport
    ? "transition-none"
    : "transition-all duration-300";
  const mobileColorTransitionClass = isMobileViewport
    ? "transition-none"
    : "transition-colors duration-200";
  const mobileTransformTransitionClass = isMobileViewport
    ? "transition-none"
    : "transition-transform duration-300";

  const activeItem =
    NAV_ITEMS.find((item) => item.label === activeMenu) ?? null;
  const ActiveDesktopDropdown = activeItem?.DesktopDropdown ?? null;

  const openDesktopMenu = (menuLabel) => {
    if (!menuLabel) {
      return;
    }

    setActiveMenu(menuLabel);
  };

  const closeDesktopMenu = () => {
    setActiveMenu(null);
  };

  function closeAllMenus() {
    closeDesktopMenu();
    setMobileOpen(false);
    setMobileExpanded("");
  }

  const closeAllMenusEvent = useEffectEvent(() => {
    closeAllMenus();
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const updateViewportState = () => {
      setIsMobileViewport(mediaQuery.matches);
    };

    updateViewportState();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateViewportState);

      return () => {
        mediaQuery.removeEventListener("change", updateViewportState);
      };
    }

    mediaQuery.addListener(updateViewportState);

    return () => {
      mediaQuery.removeListener(updateViewportState);
    };
  }, []);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setMotionPathname(pathname);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [pathname]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeAllMenusEvent();
      }
    };

    const handlePointerDown = (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      if (!event.target.closest("[data-header-shell='true']")) {
        closeAllMenusEvent();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  useLayoutEffect(() => {
    const updateScrolledState = () => {
      setIsScrolled(window.scrollY > 24);
    };

    const frameId = window.requestAnimationFrame(updateScrolledState);

    window.addEventListener("scroll", updateScrolledState, { passive: true });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", updateScrolledState);
    };
  }, [pathname]);

  useEffect(() => {
    if (activeMenu === null) {
      return undefined;
    }

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      // Close the open desktop dropdown once the user scrolls a meaningful
      // amount, since it currently only closes on mouse-leave and otherwise
      // stays stuck open while the page scrolls underneath it.
      if (Math.abs(window.scrollY - lastScrollY) > 4) {
        setActiveMenu(null);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeMenu]);

  useEffect(() => {
    if (!mobileOpen) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverscroll = document.body.style.overscrollBehavior;
    const previousHtmlOverscroll =
      document.documentElement.style.overscrollBehavior;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overscrollBehavior = previousBodyOverscroll;
      document.documentElement.style.overscrollBehavior =
        previousHtmlOverscroll;
    };
  }, [mobileOpen]);

  const handleNavigate = () => {
    closeAllMenus();
  };

  return (
    <header className="pointer-events-none fixed top-4 w-screen md:inset-x-0 px-5 sm:px-10 z-999 sm:right-10 sm:left-10">
      <div className="mx-auto flex w-full min-w-0 max-w-full flex-col gap-3">
        <motion.div
          data-header-shell="true"
          initial={false}
          layout={!suppressDesktopMotion}
          transition={desktopShellTransition}
          animate={{
            paddingTop: isCompact ? 0 : 14,
            paddingBottom: isCompact ? 0 : 14,
            paddingLeft: isCompact ? 18 : 10,
            paddingRight: isCompact ? 18 : 10,
          }}
          className={`pointer-events-auto relative mx-auto hidden w-full items-center justify-between rounded-full ${desktopShellTransitionClass} lg:flex ${
            isCompact ? "max-w-350 " : "max-w-full "
          } ${
            isCompact
              ? " bg-black/50 backdrop-blur-xl"
              : " bg-transparent shadow-none"
          }`}
        >
          <Link
            href="/"
            onClick={handleNavigate}
            className="relative z-20 mr-4 flex shrink-0 items-center rounded-full"
          >
            <motion.div
              initial={false}
              animate={{ scale: isCompact ? 0.9 : 1 }}
              transition={SHELL_SPRING}
              className="origin-left"
            >
              <Image
                src={
                  isCompact
                    ? "/images/logo/sml-logo-wide-white-svg.svg"
                    : "/images/logo/sml-logo-wide-svg.svg"
                }
                alt="Safety Management Ltd"
                width={176}
                height={48}
                className={`w-40 ${isCompact ? "sm:w-20 py-3" : ""}`}
                priority
              />
            </motion.div>
          </Link>

          <div
            className="relative flex flex-1 items-center justify-center"
            onMouseLeave={closeDesktopMenu}
          >
            <motion.nav
              initial={false}
              layout={!suppressDesktopMotion}
              transition={desktopShellTransition}
              className={`relative flex items-center justify-center gap-1 rounded-full px-8 ${
                isCompact ? "" : "bg-transparent"
              }`}
            >
              {NAV_ITEMS.map((item) => {
                const isActive = activeMenu === item.label;
                const isPlatformItem = item.label === "SML Connect";

                return (
                  <div key={item.label} className="relative">
                    {isActive ? (
                      <motion.span
                        layoutId="desktop-navbar-highlight"
                        className="absolute inset-0 "
                        transition={PANEL_SPRING}
                      />
                    ) : null}
                    <motion.button
                      initial={false}
                      layout={!suppressDesktopMotion}
                      type="button"
                      aria-label={item.label}
                      aria-expanded={isActive}
                      aria-controls={`${menuId}-${item.label}`}
                      aria-haspopup="menu"
                      onMouseEnter={() => openDesktopMenu(item.label)}
                      onFocus={() => openDesktopMenu(item.label)}
                      onClick={() => {
                        if (isPlatformItem) {
                          closeDesktopMenu();
                          router.push("/sml-connect");
                          return;
                        }

                        if (isActive) {
                          closeDesktopMenu();
                          return;
                        }

                        openDesktopMenu(item.label);
                      }}
                      transition={desktopShellTransition}
                      className={`relative z-10 flex cursor-pointer items-center gap-2 rounded-full border-2 border-transparent font-medium tracking-[-0.02em] text-white transition-colors duration-200 hover:text-white hover:border-primary ${
                        isCompact
                          ? "px-4 py-2 text-lg"
                          : "px-5 py-3 text-[1.24rem]"
                      }`}
                    >
                      {isPlatformItem ? (
                        <Image
                          src="/images/logo/connect/sml-connect-logo-light.svg"
                          alt="SML Connect"
                          width={120}
                          height={30}
                          className={`h-auto ${isCompact ? "w-26" : "w-30"}`}
                        />
                      ) : (
                        <span>{item.label}</span>
                      )}

                      <FAIcon
                        icon="chevron-down"
                        size={16}
                        className={`transition-transform duration-300 ${
                          isActive ? "rotate-180 text-white" : "text-white/72"
                        }`}
                      />
                    </motion.button>
                  </div>
                );
              })}
              <Link
                href="/contact#contact-form"
                onClick={handleNavigate}
                className={`relative z-10 flex cursor-pointer items-center gap-2 rounded-full border-2 border-transparent font-medium tracking-[-0.02em] text-white transition-colors duration-200 hover:text-white hover:border-primary ${
                  isCompact ? "px-4 py-2 text-lg" : "px-5 py-3 text-[1.24rem]"
                }`}
              >
                <span>Contact</span>
              </Link>
            </motion.nav>

            <AnimatePresence initial={false} mode="wait">
              {activeItem ? (
                <motion.div
                  key={activeItem.label}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={DROPDOWN_FADE_TRANSITION}
                  className="pointer-events-auto absolute top-full left-1/2 z-50 -translate-x-1/2 px-6 pt-4 pb-5"
                >
                  {ActiveDesktopDropdown ? (
                    <ActiveDesktopDropdown
                      id={`${menuId}-${activeItem.label}`}
                      onNavigate={handleNavigate}
                    />
                  ) : null}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <motion.button
              type="button"
              initial={false}
              onClick={() => {
                window.open(
                  "https://portal.safety-management.co.uk/login",
                  "_blank",
                );
              }}
              animate={{
                height: isCompact ? 46 : 56,
                paddingLeft: isCompact ? 24 : 32,
                paddingRight: isCompact ? 24 : 32,
              }}
              transition={desktopShellTransition}
              className="inline-flex cursor-pointer items-center justify-center rounded-full bg-primary text-sm uppercase font-semibold text-black transition-transform duration-200"
            >
              Login
              <FAIcon
                icon="arrow-right-to-bracket"
                size={16}
                noMargin
                className="ml-2"
              />
            </motion.button>
          </div>
        </motion.div>

        <motion.div
          data-header-shell="true"
          initial={false}
          transition={isMobileViewport ? INSTANT_TRANSITION : SHELL_SPRING}
          className={`pointer-events-auto ${mobileTransitionClass} lg:hidden  ${
            mobileOpen
              ? "fixed inset-0 z-1000 flex flex-col overflow-hidden px-4 pt-4 pb-3 bg-zinc-100"
              : "relative mx-auto flex w-full min-w-0 max-w-full self-center flex-col overflow-hidden rounded-[1.125rem] border border-white/25 bg-[#06040c]/60 px-2.5 py-2.5 shadow-[0_12px_40px_rgba(6,4,12,0.24)] backdrop-blur-xl"
          }`}
        >
          <div
            className={`flex min-w-0 items-center justify-between gap-3 ${
              mobileOpen ? "border-b border-white/10 pb-3" : ""
            }`}
          >
            <Link
              href="/"
              onClick={handleNavigate}
              className="flex min-w-0 flex-1 items-center rounded-full pr-2"
            >
              <motion.div
                initial={false}
                animate={{ scale: mobileOpen ? 1 : isCompact ? 0.94 : 1 }}
                transition={
                  isMobileViewport ? INSTANT_TRANSITION : SHELL_SPRING
                }
                className="max-w-full origin-left"
              >
                <Image
                  src={
                    mobileOpen
                      ? "/images/logo/sml-logo-wide-svg.svg"
                      : "/images/logo/sml-logo-wide-white-svg.svg"
                  }
                  alt="Safety Management Ltd"
                  width={152}
                  height={42}
                  className={`h-auto max-w-full ${
                    mobileOpen ? "w-18 sm:w-20" : "w-15 sm:w-17"
                  }`}
                  priority
                />
              </motion.div>
            </Link>

            <button
              type="button"
              aria-expanded={mobileOpen}
              aria-controls={`${menuId}-mobile`}
              aria-label={
                mobileOpen ? "Close navigation menu" : "Open navigation menu"
              }
              onClick={() => {
                if (mobileOpen) {
                  closeAllMenus();
                  return;
                }

                setMobileExpanded("");
                setMobileOpen(true);
              }}
              className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${mobileColorTransitionClass} ${
                mobileOpen
                  ? "bg-white text-[#06040c]"
                  : "bg-white/90 text-[#06040c] shadow-[0_3px_12px_rgba(6,4,12,0.18)]"
              }`}
            >
              {mobileOpen ? (
                <CloseIcon className="h-5 w-5" />
              ) : (
                <MenuIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          <AnimatePresence initial={false}>
            {mobileOpen ? (
              <motion.div
                id={`${menuId}-mobile`}
                initial={isMobileViewport ? false : { opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={isMobileViewport ? undefined : { opacity: 0, y: -10 }}
                transition={
                  isMobileViewport
                    ? INSTANT_TRANSITION
                    : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }
                }
                className="mt-2 flex min-h-0 flex-1 flex-col"
              >
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
                  <nav className="space-y-0.5 pb-4">
                    {NAV_ITEMS.map((item) => {
                      const isExpanded = mobileExpanded === item.label;
                      const MobileContent = item.MobileContent ?? null;

                      return (
                        <div
                          key={item.label}
                          className="border-b border-black/10 last:border-b-0"
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setMobileExpanded((currentItem) =>
                                currentItem === item.label ? "" : item.label,
                              )
                            }
                            className="flex w-full min-w-0 items-center justify-between gap-4 py-3 text-left"
                          >
                            {item.label === "SML Connect" ? (
                              <Image
                                src="/images/logo/connect/sml-connect-logo.svg"
                                alt="SML Connect"
                                width={120}
                                height={30}
                                className="h-auto w-26"
                              />
                            ) : (
                              <span className="min-w-0 text-[1.18rem] font-medium leading-[1.02] tracking-[-0.045em] text-black">
                                {item.label}
                              </span>
                            )}
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-black/50">
                              <ChevronDownIcon
                                className={`h-3 w-3 text-black ${mobileTransformTransitionClass} ${
                                  isExpanded ? "rotate-180" : ""
                                }`}
                              />
                            </span>
                          </button>

                          <AnimatePresence initial={false}>
                            {isExpanded ? (
                              <motion.div
                                initial={
                                  isMobileViewport ? false : { height: 0 }
                                }
                                animate={{ height: "auto" }}
                                exit={
                                  isMobileViewport ? undefined : { height: 0 }
                                }
                                transition={
                                  isMobileViewport
                                    ? INSTANT_TRANSITION
                                    : {
                                        duration: 0.18,
                                        ease: "easeOut",
                                      }
                                }
                                className={`overflow-hidden ${DROPDOWN_SHADOW} rounded-lg`}
                              >
                                <div className="pb-3">
                                  {MobileContent ? (
                                    <div className="rounded-[1.1rem] bg-white/3 py-0.5">
                                      <MobileContent
                                        onNavigate={handleNavigate}
                                      />
                                    </div>
                                  ) : (
                                    <div className="space-y-1.5">
                                      {item.children.map((child) => (
                                        <Link
                                          key={child.title}
                                          href={child.href}
                                          onClick={handleNavigate}
                                          className="flex min-w-0 items-start justify-between gap-3 rounded-[1rem] bg-white/4 px-3.5 py-3 text-white transition-colors duration-200 hover:bg-white/6"
                                        >
                                          <div className="min-w-0">
                                            <p className="text-sm font-semibold text-white">
                                              {child.title}
                                            </p>
                                            <p className="mt-1 text-[0.84rem] leading-5 text-white/64">
                                              {child.description}
                                            </p>
                                          </div>
                                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/7 text-white">
                                            <FAIcon
                                              icon="arrow-right"
                                              className="h-3.5 w-3.5"
                                            />
                                          </span>
                                        </Link>
                                      ))}

                                      <Link
                                        href={item.featured.href}
                                        onClick={handleNavigate}
                                        className="block rounded-[1rem] border border-white/10 bg-white/3 px-3.5 py-3.5 text-white transition-colors duration-200 hover:bg-white/5"
                                      >
                                        <p className="text-[0.68rem] font-semibold tracking-[0.24em] text-white/52 uppercase">
                                          {item.featured.eyebrow}
                                        </p>
                                        <p className="mt-2 text-[0.98rem] font-medium leading-tight tracking-[-0.04em] text-white">
                                          {item.featured.title}
                                        </p>
                                        <span className="mt-2.5 inline-flex items-center gap-2 text-[0.8rem] font-semibold text-white">
                                          <span>{item.featured.cta}</span>
                                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/7 text-white">
                                            <FAIcon
                                              icon="arrow-right"
                                              className="h-3.5 w-3.5"
                                            />
                                          </span>
                                        </span>
                                      </Link>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            ) : null}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </nav>
                </div>

                <div className="mt-auto border-t border-white/10 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.2rem)]">
                  <div className="flex flex-col gap-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Link
                        href="/contact#contact-form"
                        onClick={handleNavigate}
                        className="flex min-w-0 items-center justify-center rounded-full bg-white px-3.5 py-2.5 text-[0.82rem] font-semibold text-[#06040c]"
                      >
                        Contact
                      </Link>

                      <button
                        type="button"
                        className="flex min-w-0 items-center justify-center rounded-full bg-primary px-3.5 py-2.5 text-[0.82rem] font-semibold text-black"
                      >
                        Login
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </div>
    </header>
  );
}
